import React from 'react';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import { useOpportunityPresetMetadata } from '../../hooks/useOpportunityPresetMetadata';
import {
  DATE_PRESETS,
  DatePresetId,
  buildPresetFilters,
  resolveDateRange,
} from '../../support/opportunityDatePresets';
import { asSingleValue } from '../../support/utils';
import type { Filter } from '../../types/user';

const ALL_DATES = 'all';

export interface SelectedDatePreset {
  id: DatePresetId;
  filters: Filter[];
}

interface Props {
  value: DatePresetId | null;
  onChange: (preset: SelectedDatePreset | null) => void;
}

const OppDatePresetSelect: React.FC<Props> = ({ value, onChange }) => {
  const { periods, periodsError, isLoading } = useOpportunityPresetMetadata();
  const today = new Date();

  // Week and month ranges need nothing from Salesforce. Quarters and the fiscal year do, so those
  // stay off until its periods are loaded rather than guessing where a fiscal quarter starts.
  const isAvailable = (id: DatePresetId, needsFiscal: boolean) =>
    !needsFiscal || resolveDateRange(id, today, periods) !== null;

  let unavailableReason = '';
  if (isLoading) unavailableReason = 'Loading Salesforce fiscal periods...';
  else if (periodsError) unavailableReason = "Couldn't load Salesforce fiscal periods for quarters and year";

  const handleChange = (selected: string) => {
    if (selected === ALL_DATES) {
      onChange(null);
      return;
    }

    const preset = DATE_PRESETS.find((p) => p.id === selected);
    if (!preset) return;

    const range = resolveDateRange(preset.id, new Date(), periods);
    if (!range) return;

    onChange({ id: preset.id, filters: buildPresetFilters(range) });
  };

  return (
    <div title={unavailableReason || undefined} className="min-w-0 w-full sm:w-52 shrink-0">
      <HbrSelect
        value={value ?? ALL_DATES}
        onHbr-change={(e) => handleChange(asSingleValue(e.target.value))}
      >
        <HbrOption value={ALL_DATES}>All dates</HbrOption>
        {DATE_PRESETS.map((preset) => (
          <HbrOption
            key={preset.id}
            value={preset.id}
            disabled={!isAvailable(preset.id, preset.needsFiscal)}
          >
            {preset.label}
          </HbrOption>
        ))}
      </HbrSelect>
    </div>
  );
};

export default OppDatePresetSelect;
